import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../api";
import { isSupplierPublicPath } from "./publicRoutes";

/**
 * Lightweight auth context. Three states for `user`:
 *   - undefined : still checking (show skeleton/spinner)
 *   - null      : verified unauthenticated → redirect to /login
 *   - object    : verified authenticated
 *
 * We deliberately skip the /auth/me check while AuthCallback is exchanging
 * a fresh session_id (would race the cookie set).
 *
 * Hard timeout + retry-loop detection: ProcOps in prod is reached via a
 * custom domain (recke.ai) that proxies /api/* to a separate backend pod.
 * If anything in that proxy path stalls (slow pod, cookie domain mismatch,
 * CORS preflight black-hole), the /auth/me call can hang indefinitely and
 * the user gets stuck on "Checking session…". We cap the call at 5s and
 * fail open to `null` so the user moves to /login instead of hanging.
 *
 * We also count consecutive failed auth-check → /login redirects per
 * browser session. If we redirect to /login more than 3 times in 60s
 * without ever successfully fetching the user, the user is genuinely
 * stuck (e.g. their browser is rejecting our session cookie). Redirect
 * them to /login?error=auth_failed so we surface a useful diagnostic
 * instead of looping silently.
 */
const AuthContext = createContext({ user: undefined, refresh: () => {}, logout: async () => {} });

const AUTH_ME_TIMEOUT_MS = 5000;
const LOOP_KEY = "procops_auth_loop";
const LOOP_THRESHOLD = 3;
const LOOP_WINDOW_MS = 60_000;

const readLoopState = () => {
  try {
    const raw = sessionStorage.getItem(LOOP_KEY);
    if (!raw) return { count: 0, first: 0 };
    const parsed = JSON.parse(raw);
    return { count: parsed.count || 0, first: parsed.first || 0 };
  } catch {
    return { count: 0, first: 0 };
  }
};

const recordAuthFailure = () => {
  try {
    const now = Date.now();
    const state = readLoopState();
    if (now - state.first > LOOP_WINDOW_MS) {
      sessionStorage.setItem(LOOP_KEY, JSON.stringify({ count: 1, first: now }));
      return 1;
    }
    const next = { count: state.count + 1, first: state.first };
    sessionStorage.setItem(LOOP_KEY, JSON.stringify(next));
    return next.count;
  } catch {
    return 0;
  }
};

const clearAuthLoop = () => {
  try { sessionStorage.removeItem(LOOP_KEY); } catch { /* ignore */ }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    // Supplier links use their own token, not an internal Google session.
    if (isSupplierPublicPath(window.location.pathname)) return;
    // CRITICAL: skip the /me check if we're mid-callback — AuthCallback will
    // exchange the session_id and set the cookie before redirecting.
    if (window.location.hash?.includes("session_id=") || window.location.pathname === "/auth/callback") {
      setUser(undefined);
      return;
    }
    const attemptMe = () => axios.get(`${API}/auth/me`, {
      withCredentials: true,
      timeout: AUTH_ME_TIMEOUT_MS,
    });
    try {
      let r;
      try {
        r = await attemptMe();
      } catch (firstErr) {
        // Only a 401 means "genuinely not signed in". Timeouts, network
        // blips, and 5xx (e.g. the backend mid-redeploy) are transient —
        // retry once before kicking a validly-logged-in user to /login.
        const status = firstErr?.response?.status;
        const transient = status === undefined || status >= 500;
        if (!transient) throw firstErr;
        await new Promise((res) => setTimeout(res, 1000));
        r = await attemptMe();
      }
      setUser(r.data);
      clearAuthLoop();
    } catch (err) {
      // A pending internal check must not redirect a newly-opened public page.
      if (isSupplierPublicPath(window.location.pathname)) return;
      // Surface to console so DevTools shows the exact failure mode
      // (timeout vs 401 vs network) — invaluable when users report being
      // stuck. Stays minimal so we don't leak anything sensitive.
      const status = err?.response?.status;
      const code = err?.code;
      // eslint-disable-next-line no-console
      console.warn(`[auth] /auth/me failed — status=${status ?? "n/a"} code=${code ?? "n/a"}`);

      const failures = recordAuthFailure();
      if (failures > LOOP_THRESHOLD) {
        // Detected loop — bail out to a diagnostic page so the user isn't
        // bounced silently forever. /login renders an error card when it
        // sees ?error=auth_failed.
        setUser(null);
        if (window.location.pathname !== "/login") {
          navigate("/login?error=auth_failed", { replace: true });
        }
        return;
      }
      setUser(null);
    }
  }, [navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, { withCredentials: true });
    } catch {
      /* ignore — best-effort */
    }
    clearAuthLoop();
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
